<script lang="ts" setup>
  import { ref, computed, onMounted, watch } from 'vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import SkeletonLoaderInlineField from '@/components/skeleton/SkeletonLoaderInlineField.vue'
  import type { Option } from '@/components/admin/form/SelectField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { useOfficeDocument } from '@/composables/useOfficeDocument'
  import {
    WordDocumentTemplatesService,
    DocumentsService,
    type TWordDocumentTemplate,
    type Document as PdfDocument
  } from '@/types/generated'
  import { ExternalLink, Eye, PencilIcon, CloudDownloadIcon } from 'lucide-vue-next'
  import { toast } from '@/components/ui/toast'
  import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
  import { Button } from '@/components/ui/button'
  import EmbeddableOfficePreview from '@/components/app/office/EmbeddableOfficePreview.vue'

  // Translation setup
  const { t } = useTranslation({
    en: {
      selectTemplate: 'Select template...',
      noTemplatesFound: 'No templates found',
      draftText: 'Draft document using template',
      forTheRequest: 'for the request.',
      documentDrafted: 'Document was drafted using template',
      wasCreatedForRequest: 'and created for the request.',
      viewDocument: 'View document',
      errorOpeningDocument: 'Error opening document'
    },
    de: {
      selectTemplate: 'Vorlage auswählen...',
      noTemplatesFound: 'Keine Vorlagen gefunden',
      draftText: 'Dokument mit Vorlage',
      forTheRequest: 'für die Anfrage erstellen.',
      documentDrafted: 'Dokument wurde mit Vorlage',
      wasCreatedForRequest: 'erstellt und für die Anfrage angelegt.',
      viewDocument: 'Dokument anzeigen',
      errorOpeningDocument: 'Fehler beim Öffnen des Dokuments'
    }
  })

  // Define props
  interface Props {
    action: any // TAgentAction type from generated types
    customPayload?: any // Optional custom payload from parent
  }

  const props = defineProps<Props>()

  // Define emits
  const emit = defineEmits<{
    payloadChanged: [payload: any]
  }>()

  // Reactive state
  const isLoadingTemplates = ref(false)
  const selectedTemplateUuid = ref<string>('')
  const templates = ref<TWordDocumentTemplate[]>([])

  // Office document handling
  const {
    isOfficeFile: isOfficeFileUtil,
    isEditableDocument: isEditableDocumentUtil,
    openOfficeEditor
  } = useOfficeDocument()

  // Office preview state
  const showOfficePreview = ref(false)
  const currentOfficeDocument = ref<PdfDocument | null>(null)

  // Computed properties
  const templateOptions = computed<Option[]>(() => {
    return templates.value.map((template) => ({
      value: template.uuid || '',
      title: template.name || 'Untitled Template',
      icon: undefined
    }))
  })

  const selectedTemplate = computed(() => {
    return templates.value.find((template) => template.uuid === selectedTemplateUuid.value)
  })

  // Get document UUID from commit return value
  const documentUuid = computed(() => {
    return props.action?.commit_return_value?.document_uuid
  })

  // Check if action should show summary (committed, reverted, or has error)
  const shouldShowSummary = computed(
    () =>
      props.action?.is_committed === true ||
      props.action?.is_reverted === true ||
      props.action?.has_commit_error === true ||
      (props.action?.is_commitable === false && selectedTemplateUuid.value)
  )

  // Summary text for committed/cancelled/error actions
  const summaryText = computed(() => {
    if (!shouldShowSummary.value || !selectedTemplate.value) return ''

    const templateName = selectedTemplate.value.name || 'Untitled Template'
    return `${t('documentDrafted')} "${templateName}" ${t('wasCreatedForRequest')}`
  })

  // Methods
  const isOfficeFile = (document: any) => {
    return isOfficeFileUtil(document)
  }

  const isEditableDocument = (document: { document_type: string; filename?: string }) => {
    return isEditableDocumentUtil(document)
  }

  const openOfficeFile = (document: any) => {
    openOfficeEditor(document)
  }

  const previewDocument = async (document: any) => {
    try {
      if (isOfficeFile(document)) {
        currentOfficeDocument.value = document as PdfDocument
        showOfficePreview.value = true
      } else {
        // Fallback to opening in new tab for non-office files
        if (document.url) {
          window.open(document.url, '_blank')
        }
      }
    } catch (error) {
      console.error('Error opening document preview:', error)
      toast({
        variant: 'destructive',
        title: t('errorOpeningDocument'),
        description: t('errorOpeningDocument')
      })
    }
  }

  const previewOfficeDocument = async (document: any) => {
    try {
      if (document) {
        currentOfficeDocument.value = document as PdfDocument
        showOfficePreview.value = true
      } else {
        toast({
          variant: 'destructive',
          title: t('errorOpeningDocument'),
          description: t('errorOpeningDocument')
        })
      }
    } catch (error) {
      console.error('Error opening Office document preview:', error)
      toast({
        variant: 'destructive',
        title: t('errorOpeningDocument'),
        description: t('errorOpeningDocument')
      })
    }
  }

  const openDocument = async () => {
    if (!documentUuid.value) return

    try {
      const response = await DocumentsService.documentsRetrieve({ uuid: documentUuid.value })
      if (response) {
        // Check if it's an Office file and handle accordingly
        if (isOfficeFile(response)) {
          openOfficeFile(response)
        } else {
          // Fallback to opening in new tab
          if (response.url) {
            window.open(response.url, '_blank')
          } else {
            toast({
              variant: 'destructive',
              title: t('errorOpeningDocument'),
              description: t('errorOpeningDocument')
            })
          }
        }
      } else {
        toast({
          variant: 'destructive',
          title: t('errorOpeningDocument'),
          description: t('errorOpeningDocument')
        })
      }
    } catch (error) {
      console.error('Error opening document:', error)
      toast({
        variant: 'destructive',
        title: t('errorOpeningDocument'),
        description: t('errorOpeningDocument')
      })
    }
  }

  const fetchTemplates = async () => {
    isLoadingTemplates.value = true
    try {
      const response = await WordDocumentTemplatesService.wordDocumentTemplatesList({
        limit: 100,
        status: 'approved' // Only fetch approved templates
      })
      templates.value = response.results || []
    } catch (error) {
      console.error('Error fetching word document templates:', error)
      templates.value = []
    } finally {
      isLoadingTemplates.value = false
    }
  }

  const handleTemplateSelection = (templateUuid: string) => {
    selectedTemplateUuid.value = templateUuid
    emitPayloadChange()
  }

  // Emit payload changes to parent
  const emitPayloadChange = () => {
    if (props.action?.payload?.case_uuid && selectedTemplateUuid.value) {
      const payload = {
        case_uuid: props.action.payload.case_uuid,
        word_document_template_uuid: selectedTemplateUuid.value
      }
      emit('payloadChanged', payload)
    }
  }

  // Initialize selections from action payload
  const initializeFromPayload = (payload: any) => {
    if (!payload) return

    if (payload.word_document_template_uuid) {
      selectedTemplateUuid.value = payload.word_document_template_uuid
    }
  }

  // Watch for action payload changes
  watch(
    () => props.action?.payload,
    (newPayload) => {
      initializeFromPayload(newPayload)
    },
    { deep: true }
  )

  // Watch for custom payload changes
  watch(
    () => props.customPayload,
    (newCustomPayload) => {
      if (newCustomPayload) {
        initializeFromPayload(newCustomPayload)
      }
    },
    { deep: true }
  )

  // Initialize component
  onMounted(async () => {
    // Set initial selection from action payload or custom payload
    initializeFromPayload(props.customPayload || props.action?.payload)

    // Fetch available templates
    await fetchTemplates()

    // Emit initial payload if template is selected
    if (selectedTemplateUuid.value) {
      emitPayloadChange()
    }
  })
</script>

<template>
  <div>
    <!-- Action Summary (committed, cancelled, reverted, or error) -->
    <div
      v-if="shouldShowSummary"
      class="space-y-3"
    >
      <div class="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        <span>{{ summaryText }}</span>
        <!-- Document action buttons for committed actions -->
        <div
          v-if="props.action?.is_committed && documentUuid"
          class="ml-2 flex items-center gap-2"
        >
          <!-- Edit button for Office documents (Word docs created by this action are always editable) -->
          <Button
            variant="secondary"
            size="sm"
            class="h-7 px-2 text-xs"
            :title="'Dokument in Office Editor bearbeiten'"
            @click="
              async () => {
                const response = await DocumentsService.documentsRetrieve({ uuid: documentUuid })
                openOfficeFile(response)
              }
            "
          >
            <PencilIcon
              :size="12"
              class="mr-1"
            />
            Bearbeiten
          </Button>

          <!-- Preview button for Office documents -->
          <Button
            variant="secondary"
            size="sm"
            class="h-7 px-2 text-xs"
            :title="'Dokument ansehen'"
            @click="
              async () => {
                const response = await DocumentsService.documentsRetrieve({ uuid: documentUuid })
                previewOfficeDocument(response)
              }
            "
          >
            <Eye
              :size="12"
              class="mr-1"
            />
            Ansehen
          </Button>

          <!-- Download button -->
          <Button
            variant="secondary"
            size="sm"
            class="h-7 px-2 text-xs"
            :title="'Dokument herunterladen'"
            @click="
              async () => {
                const response = await DocumentsService.documentsRetrieve({ uuid: documentUuid })
                if (response.url) {
                  window.open(response.url, '_blank')
                }
              }
            "
          >
            <CloudDownloadIcon
              :size="12"
              class="mr-1"
            />
            Herunterladen
          </Button>
        </div>
      </div>
    </div>

    <!-- Action Details (when editable) -->
    <div
      v-else
      class="space-y-3"
    >
      <div class="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        <span>{{ t('draftText') }}</span>

        <!-- Template Selection -->
        <template v-if="isLoadingTemplates">
          <SkeletonLoaderInlineField
            width="200px"
            height="32px"
            class="inline-block"
          />
        </template>
        <template v-else>
          <SelectField
            v-model="selectedTemplateUuid"
            variant="inline-text"
            :options="templateOptions"
            :is-filterable="true"
            :disabled="isLoadingTemplates"
            :placeholder="
              templateOptions.length === 0 ? t('noTemplatesFound') : t('selectTemplate')
            "
            name="template"
            @update:model-value="handleTemplateSelection"
          />
        </template>

        <span>{{ t('forTheRequest') }}</span>
      </div>
    </div>
  </div>

  <!-- Office Document Preview Modal -->
  <Dialog v-model:open="showOfficePreview">
    <DialogContent class="h-[85vh] w-full max-w-5xl overflow-hidden p-0">
      <div class="flex h-full flex-col">
        <DialogHeader class="border-b px-6 py-4">
          <DialogTitle>
            {{
              currentOfficeDocument?.title || currentOfficeDocument?.filename || 'Document Preview'
            }}
          </DialogTitle>
        </DialogHeader>
        <div class="w-full flex-1 overflow-hidden">
          <EmbeddableOfficePreview
            v-if="currentOfficeDocument"
            :document="currentOfficeDocument"
            height="100%"
            :allow-download="false"
            :allow-print="false"
            :show-border="false"
          />
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>

<style scoped></style>
